import dayjs from 'dayjs';
import { departureLocalOf } from './localTime';

import type { AccessLegs, NormalizedQuote } from '../../../apis/lanePlanner';
import {
  carrierOf,
  formatMoney,
  MODE_TITLES,
  pickupLabelOf,
  routeOf,
  travelTextOf,
} from './quoteView';
import { TruckingChoices, truckingShareLines } from './truckingView';

/** The quote's own airport or port pair, else the places searched. */
const laneOf = (
  quote: NormalizedQuote,
  origin?: string,
  destination?: string
): string | null =>
  routeOf(quote) ??
  (origin && destination ? `${origin} → ${destination}` : null);

/** Plain-text summary of a quote for pasting into an email or chat. */
export const quoteShareText = (
  quote: NormalizedQuote,
  origin?: string,
  destination?: string,
  shipDate?: string,
  accessLegs?: AccessLegs,
  truckingChoices?: TruckingChoices
): string => {
  const departs = departureLocalOf(quote);
  // a road quote has no departure: its pickup is the search's ship date
  const pickup = departs ? null : pickupLabelOf(quote, shipDate);
  const transit = travelTextOf(quote);
  const lines = [
    `${MODE_TITLES[quote.mode]} quote: ${carrierOf(quote)}`,
    laneOf(quote, origin, destination)
      ? `Lane: ${laneOf(quote, origin, destination)}`
      : null,
    departs
      ? `Departs: ${departs.date.replace(',', '')} ${departs.time}${departs.zone ? ` ${departs.zone}` : ''}`
      : null,
    pickup ? `Pickup: ${pickup.replace(',', '')}` : null,
    transit ? `Transit: ${transit}` : null,
    `Price: ${formatMoney(quote.price, quote.currency)} (estimated)`,
    ...truckingShareLines(quote, accessLegs, truckingChoices, shipDate),
    quote.valid_until
      ? `Valid until: ${dayjs(quote.valid_until).format('M/D/YY')}`
      : null,
    `Ref: ${quote.vendor_quote_id}`,
  ];
  return lines.filter(Boolean).join('\n');
};
